(() => {
  'use strict';

  const TITLE_PARAM = 'filter.p.m.custom.title_';
  const TYPE_PARAM = 'filter.p.product_type';
  const cache = new Map();

  function init(root = document) {
    const form = root.querySelector('#header-search-custom');
    if (!form || form.dataset.cantripReady) return;
    form.dataset.cantripReady = 'true';
    const input = form.querySelector('input[name="q"]');
    const select = form.querySelector('#custom_search_options');
    const board = form.querySelector('.custom-floating-board');
    const status = form.querySelector('[data-search-status]');
    if (!input || !select || !board) return;

    function syncTitles(preserve = true) {
      const previous = select.value;
      const configured = document.getElementById('search-keyword')?.content.querySelectorAll('option') || [];
      select.replaceChildren(new Option('全タイトル', ''));
      const known = new Set(['']);
      for (const option of configured) {
        const value = option.value.trim();
        if (value && !known.has(value)) { select.add(new Option(option.textContent.trim(), value)); known.add(value); }
      }
      const params = new URLSearchParams(location.search);
      const isSearch = location.pathname.replace(/\/$/, '') === new URL(form.action).pathname.replace(/\/$/, '');
      const wanted = preserve ? previous : (isSearch ? params.get(TITLE_PARAM) || '' : form.dataset.defaultTitle || '');
      select.value = known.has(wanted) ? wanted : '';
      if (!preserve && isSearch && params.has('q')) input.value = params.get('q');
    }
    syncTitles(false);
    document.addEventListener('shopify:section:load', () => syncTitles());
    document.addEventListener('shopify:section:reorder', () => syncTitles());

    let timer;
    let controller;
    let revision = 0;
    let composing = false;
    let selected = -1;
    let requestState = '';

    function hide() {
      board.classList.remove('active');
      board.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      selected = -1;
    }

    function cancel() {
      clearTimeout(timer);
      revision += 1;
      controller?.abort();
      form.removeAttribute('aria-busy');
    }

    function urlFor(query, title, cardType = '') {
      const url = new URL(form.action);
      url.searchParams.set('q', query);
      url.searchParams.set('type', 'product');
      url.searchParams.set('options[prefix]', 'last');
      url.searchParams.set('options[unavailable_products]', 'last');
      if (title) url.searchParams.set(TITLE_PARAM, title);
      if (cardType) url.searchParams.set(TYPE_PARAM, cardType);
      return url;
    }

    function show(html, key) {
      if (key !== JSON.stringify([input.value.trim(), select.value])) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const list = doc.querySelector('#cantrip-search-options');
      if (!list) throw new Error('Suggestion section missing');
      // A second check prevents a bad or stale server response crossing games.
      if (select.value) list.querySelectorAll('[data-card-type]').forEach(item => {
        if (item.dataset.game !== select.value) item.remove();
      });
      const count = list.querySelectorAll('[data-card-type]').length;
      board.replaceChildren(list);
      requestState = key;
      selected = -1;
      if (!count || document.activeElement !== input) {
        hide();
      } else {
        board.hidden = false;
        board.classList.add('active');
        input.setAttribute('aria-expanded', 'true');
      }
      if (status) status.textContent = count ? `${count}件のカード名候補があります。` : '';
    }

    async function request() {
      const query = input.value.trim();
      if (!query || composing) { hide(); return; }
      const title = select.value;
      const key = JSON.stringify([query, title]);
      const serial = ++revision;
      controller?.abort();
      if (cache.has(key)) { show(cache.get(key), key); return; }
      controller = new AbortController();
      form.setAttribute('aria-busy', 'true');
      try {
        const url = urlFor(query, title);
        url.searchParams.set('section_id', 'cantrip-search-suggestions');
        const response = await fetch(url, {signal: controller.signal, credentials: 'same-origin'});
        if (!response.ok) throw new Error(`Suggestions: ${response.status}`);
        const html = await response.text();
        if (serial !== revision) return;
        show(html, key);
        cache.set(key, html);
        if (cache.size > 40) cache.delete(cache.keys().next().value);
      } catch (error) {
        if (error.name !== 'AbortError' && serial === revision) {
          hide();
          if (status) status.textContent = '候補を取得できませんでした。そのまま検索できます。';
        }
      } finally {
        if (serial === revision) form.removeAttribute('aria-busy');
      }
    }

    function schedule() {
      cancel();
      hide();
      if (!composing && input.value.trim()) timer = setTimeout(request, 120);
    }

    function choose(option) {
      if (!option || requestState !== JSON.stringify([input.value.trim(), select.value])) return;
      const game = select.value || option.dataset.game || '';
      cancel();
      input.value = option.dataset.cardType || option.textContent.trim();
      location.assign(urlFor(input.value, game, option.dataset.cardType).href);
    }

    input.addEventListener('input', schedule);
    input.addEventListener('compositionstart', () => { composing = true; cancel(); hide(); });
    input.addEventListener('compositionend', () => { composing = false; schedule(); });
    select.addEventListener('change', () => { cancel(); hide(); input.focus(); request(); });
    input.addEventListener('focus', () => { if (input.value.trim()) request(); });
    input.addEventListener('keydown', event => {
      if (composing || event.isComposing) return;
      if (event.key === 'Escape') { cancel(); hide(); return; }
      const options = [...board.querySelectorAll('[data-card-type]')];
      if (!board.classList.contains('active') || !options.length) return;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        selected = (selected + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length;
        options.forEach((option, index) => option.setAttribute('aria-selected', String(index === selected)));
        input.setAttribute('aria-activedescendant', options[selected].id);
        options[selected].scrollIntoView({block: 'nearest'});
      } else if (event.key === 'Enter' && selected >= 0) {
        event.preventDefault();
        choose(options[selected]);
      }
    });
    board.addEventListener('pointerdown', event => {
      const option = event.target.closest('[data-card-type]');
      if (option) { event.preventDefault(); choose(option); }
    });
    document.addEventListener('pointerdown', event => { if (!form.contains(event.target)) { cancel(); hide(); } });
    form.addEventListener('focusout', event => { if (!form.contains(event.relatedTarget)) hide(); });
    form.addEventListener('submit', event => {
      if (composing) { event.preventDefault(); return; }
      cancel();
      hide();
      // A fresh search intentionally clears the previous card-type refinement.
    });
    hide();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
})();

// Search availability applies to Shopify's complete result set before pagination.
(() => {
  const initAvailability = () => {
    const select = document.querySelector('[data-cantrip-availability]');
    if (!select || select.dataset.bound) return;
    select.dataset.bound = 'true';
    const current = new URL(window.location.href);
    select.value = current.searchParams.get('options[unavailable_products]') === 'hide' ? 'hide' : 'last';
    select.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('options[unavailable_products]', select.value);
      url.searchParams.delete('page');
      window.location.assign(url.toString());
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAvailability, {once:true});
  else initAvailability();
})();
